import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { Exception, PaymentExternalData } from '@cargos/sprintpay-models';
import { SubscribeToAWBParams } from '@cargos/sprintpay-services';
import dayjs from 'dayjs';
import { Observable, Subject, finalize, from, of, switchMap, take, takeUntil, throwError } from 'rxjs';
import { LogoFacilityComponent } from 'src/app/shared/components';
import { DynamicDialogService } from 'src/app/shared/components/dynamic-dialog/services/dynamic-dialog.service';
import { MatStrokedButtonBorderDirective } from 'src/app/shared/directives/mat-stroked-button-border.directive';
import { SanitizerPipe } from 'src/app/shared/pipes';
import {
    CurrentFacilityService,
    CustomerService,
    DynamicsFieldsService,
    PaymentCartBuilder,
    SessionService,
    SubscriptionAWBService,
    SummaryService,
} from 'src/app/shared/services';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { AutoPayPolicyComponent } from '../auto-pay-policy/auto-pay-policy.component';
import { AutoPayComponent } from '../auto-pay/auto-pay.component';
import { AutoPayFormGroupType } from '../auto-pay/models/auto-pay-form';
import { MawbSubscriptionComponent } from '../mawb-subscription/mawb-subscription.component';
import { MAWBSubscriptionFormGroupType } from '../mawb-subscription/models/mawb-subscription-form';
import { AwbSubscriptionService } from '../services/awb-subscription.service';

interface DynamicValues {
    id: string;
    value: string;
}

@Component({
    selector: 'app-subscription-autopay',
    standalone: true,
    imports: [
        MatIconModule,
        MatButtonModule,
        LogoFacilityComponent,
        MatDialogModule,
        MatStrokedButtonBorderDirective,
        MawbSubscriptionComponent,
        AutoPayComponent,
        MatCheckboxModule,
        SanitizerPipe,
        MatDividerModule,
        MatProgressSpinnerModule,
        AutoPayPolicyComponent,
    ],
    templateUrl: './subscription-autopay.component.html',
    styleUrl: './subscription-autopay.component.scss',
})
export class SubscriptionAutopayComponent implements OnInit, AfterViewInit {
    public subscribing = false;
    private unsubscribe$: Subject<void>;
    public subscriptionForm: MAWBSubscriptionFormGroupType;
    public autoPayForm: AutoPayFormGroupType;
    public awb: string | null;
    public isAllowedToContinue = true;
    public facilityName: string;
    public privacyAndPolicyURL = '';
    public isAuthenticated = false;
    public showButtonSubmit = true;
    private facilitySource: string = '';

    get emailAutoPay(): AbstractControl<string> | null {
        return this.autoPayForm?.get('email');
    }

    get isSelectedAutoPay(): AbstractControl<boolean> | null {
        return this.autoPayForm?.get('isSelected');
    }

    get isSelectedAutoPayValue(): boolean | undefined {
        return this.autoPayForm?.get('isSelected')?.value;
    }

    get emailSubscription(): AbstractControl<string> | null {
        return this.subscriptionForm?.get('email');
    }

    get isSelectedSubscription(): AbstractControl<boolean> | null {
        return this.subscriptionForm?.get('isSelected');
    }

    constructor(
        private subscriptionAWBService: SubscriptionAWBService,
        private awbSubscriptionService: AwbSubscriptionService,
        private dynamicDialogService: DynamicDialogService,
        private router: Router,
        private summaryService: SummaryService,
        private currentFacilityService: CurrentFacilityService,
        private sessionService: SessionService,
        private dynamicsFieldsService: DynamicsFieldsService,
        private customerService: CustomerService,
        private changeDetectorRef: ChangeDetectorRef,
        private paymentCartBuilder: PaymentCartBuilder
    ) {
        this.unsubscribe$ = new Subject<void>();
        this.awb = this.summaryService.getMAWB();
        this.facilityName = this.currentFacilityService.getFacilityName();
        this.facilitySource = this.currentFacilityService.getFacilitySource();
    }

    ngOnInit(): void {
        this.resetInformation();
        this.subscribeSubscription();
        this.subscribeAutoPay();
        this.getPrivacyAndPolicyUrl();
        this.subscribeIsAuthenticated();
    }

    ngAfterViewInit(): void {
        // TODO: There are many isValid fucntions, validate that are needed
        setTimeout(() => {
            this.subscribeIsAllowedToContinue();
            this.subscribeIsValidAutoPay();
            this.changeDetectorRef.detectChanges();
        });
    }

    resetInformation(): void {
        this.awbSubscriptionService.setPolicy(false);
    }

    submit(): void {
        if (this.subscribing) {
            return;
        }
        this.subscribing = true;

        this.isAllowedSubscription()
            .pipe(
                take(1),
                switchMap(() => this.subscribe()),
                switchMap(() => this.modalSuccess()),
                finalize(() => {
                    this.subscribing = false;
                })
            )
            .subscribe({
                next: (result) => {
                    const type = this.isSelectedAutoPay?.value ? 'Autopay/subscription' : 'Subscription';
                    this.awbSubscriptionService.showTooltip(`Your ${type} request has been activated successfully!`);

                    if (result.value) {
                        const isSubscriptionWithoutRCForNFD =
                            this.awbSubscriptionService.getIsEnabledSubscriptionWithoutRCForNFD();

                        if (!isSubscriptionWithoutRCForNFD) {
                            Swal.close();
                        } else {
                            const location = this.summaryService.getLocation();
                            const awb = this.summaryService.getMAWB();
                            this.router.navigate([`/search-awb/details`], {
                                queryParams: {
                                    mawb: awb,
                                    location,
                                },
                            });
                        }
                    }
                },
                error: (error: Exception) => {
                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: error.type,
                    });
                },
            });
    }

    private isAllowedSubscription(): Observable<boolean> {
        if (this.isSelectedSubscription?.value && !this.isSelectedAutoPayValue) {
            return of(this.subscriptionForm.valid);
        }

        return this.awbSubscriptionService.isAllowedToContinue();
    }

    private subscribeAWB(subscribeData: SubscribeToAWBParams, locationId?: number): Observable<string> {
        return this.subscriptionAWBService.subscribeToAWB(subscribeData, locationId).pipe(
            switchMap((response) => {
                if (response.isAutoPaySubscriptionExist || response.isArrivalSubscriptionExist) {
                    return throwError(() =>
                        Exception.fromJson({
                            description: response.message,
                            type: 'error',
                        })
                    );
                }

                return of('Completed');
            })
        );
    }

    getFirstLocation(): number {
        return (
            this.customerService.getFirstLocationId() ||
            Number(this.dynamicsFieldsService.getCollectorFormValueByKey('locationId'))
        );
    }

    private subscribe(): Observable<string> {
        if (this.isSelectedSubscription?.value && !this.isSelectedAutoPayValue) {
            return this.subscribeOnlyAWB();
        }

        return this.subscribeAndAutoPayAWB();
    }

    private subscribeOnlyAWB(): Observable<string> {
        const firstLocation = this.getFirstLocation();

        const subscriptionData: SubscribeToAWBParams = {
            awb: this.awb || '',
            sourceName: this.facilitySource,
            arrivalNotificationEmail: this.emailSubscription?.value || '',
        };

        return this.subscribeAWB(subscriptionData, firstLocation);
    }

    private subscribeAndAutoPayAWB(): Observable<string> {
        const emailAutoPay = this.emailAutoPay?.value;
        const emailSubscription = this.emailSubscription?.value ? this.emailSubscription?.value : emailAutoPay;

        const body: SubscribeToAWBParams = {
            awb: this.awb || '',
            sourceName: this.facilitySource,
            paymentType: 'ISC',
            paidTo: '',
            notificationEmail: emailSubscription || '',
            isAutoPay: this.isSelectedAutoPay?.value || false,
            arrivalNotificationEmail: emailSubscription || '',
            autoPayNotificationEmail: emailAutoPay || '',
        };
        const externalData = this.getExternalData();
        const complementaryData = this.getComplementaryData(body);
        const firstLocation = this.getFirstLocation();

        return this.subscribeAWB({ ...complementaryData, externalData }, firstLocation);
    }

    getDynamicFieldsAndValues(): DynamicValues[] {
        const dynamicValues = this.dynamicsFieldsService.getDynamicFieldsAndValues();
        let resultDynamicValues: DynamicValues[] = [];
        for (const [key, value] of Object.entries(dynamicValues)) {
            resultDynamicValues.push({ id: key, value: value });
        }
        return resultDynamicValues;
    }

    getComplementaryData(complementaryData: SubscribeToAWBParams): SubscribeToAWBParams {
        const dynamicValues = this.getDynamicFieldsAndValues();
        const customerReference = this.dynamicsFieldsService.getStaticFieldValueByKey('customerRef');
        const companyId = this.customerService.getCompanyId();
        const source = this.currentFacilityService.getFacilitySource();

        const complementaryChargeData = {
            ...complementaryData,
            companyId,
            customerReference,
            source,
            dynamicValues: dynamicValues,
        };
        return complementaryChargeData;
    }

    getExternalData(): PaymentExternalData {
        const nameFiles = this.dynamicsFieldsService.getReplacementFieldsAndValues('nameFiles');
        const data = this.paymentCartBuilder.getCommonExternalData();
        const externalData: PaymentExternalData = {
            ...data,
            awbId: this.awb,
            pickupDate: dayjs().format('YYYY-MM-DD'),
            attachments: nameFiles,
        };
        return externalData;
    }

    subscribeSubscription(): void {
        this.awbSubscriptionService
            .getSubscriptionForm()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((subscriptionForm: MAWBSubscriptionFormGroupType) => {
                this.subscriptionForm = subscriptionForm;
            });
    }

    subscribeAutoPay(): void {
        this.awbSubscriptionService
            .getAutoPayForm()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isEnableAutoPay) => {
                this.autoPayForm = isEnableAutoPay;
            });
    }

    modalSuccess(): Observable<SweetAlertResult> {
        this.dynamicDialogService.closeDialog();

        const message = this.isSelectedAutoPay?.value
            ? `Your <strong>MAWB ${this.awb} is successfully enrolled in our AutoPay feature.</strong> You’ll soon receive email notifications regarding your payment and MAWB updates. `
            : `You have successfully subscribed to receive an email when MAWB ${this.awb} has arrived`;

        return from(
            Swal.fire({
                html: message,
                icon: 'success',
                allowOutsideClick: false,
                showCancelButton: true,
                cancelButtonText: 'NEW SEARCH',
                showCloseButton: false,
                showConfirmButton: true,
                confirmButtonText: 'CONTINUE',
                reverseButtons: true,
                customClass: {
                    cancelButton:
                        'mdc-button mat-mdc-button-base h-auto mdc-button--outlined mat-mdc-outlined-button mat-primary appMatStrokedButtonBorder',
                    confirmButton:
                        'mdc-button mat-mdc-button-base my-4 mdc-button--unelevated mat-mdc-unelevated-button mat-primary',
                },
            })
        );
    }

    modalCancel(message: string): Observable<SweetAlertResult> {
        return from(
            Swal.fire({
                text: message,
                icon: 'error',
            })
        );
    }

    getPrivacyAndPolicyUrl(): void {
        this.privacyAndPolicyURL = this.currentFacilityService.getPrivacyAndPolicyURL();
    }

    subscribeIsAllowedToContinue(): void {
        this.awbSubscriptionService
            .isAllowedToContinue()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isAllowedToContinue) => {
                    this.isAllowedToContinue = isAllowedToContinue;
                },
            });
    }

    subscribeIsAuthenticated(): void {
        this.sessionService
            .isAuthenticated$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isAuthenticated: boolean) => {
                    this.isAuthenticated = isAuthenticated;
                },
            });
    }

    subscribeIsValidAutoPay(): void {
        this.awbSubscriptionService
            .isValidAutoPay()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (isValidAutoPay) => {
                    if (!this.isSelectedAutoPayValue) {
                        this.showButtonSubmit = true;
                    } else {
                        this.showButtonSubmit = isValidAutoPay;
                    }
                },
            });
    }
}
